import { Component, EventEmitter, Input, Output } from '@angular/core';
import { LocalizationService } from '@app/core/localization.service';
import { ApplicationService } from '@app/vinter/services/application.service';

type ServerPdfKind = 'detail' | 'summary' | 'ai';

/**
 * Server-side PDF download buttons (native PDFsharp-backed endpoints).
 *
 * Self-contained and fully decoupled from the legacy client-side rendering
 * components under ./pdf-report. It only needs the application id plus the
 * license/lock flags the parent already owns; the actual HTTP call, blob
 * download and per-button busy state live here. This lets the entire legacy
 * pdf-report/ folder be deleted without touching the server flow.
 */
@Component({
    selector: 'app-server-pdf-report',
    templateUrl: './server-pdf-report.component.html',
    styleUrls: ['./server-pdf-report.component.scss']
})
export class ServerPdfReportComponent {
    /** Application whose report should be generated server-side. */
    @Input() applicationId: string;
    /** License lock state owned by the parent; when locked we defer back to it. */
    @Input() isLocked: boolean;
    /** AI report license validity — mirrors the legacy AI download gating. */
    @Input() isAIReportLicenseValid = false;
    /** True once the AI report is ready to download. */
    @Input() showDownloadAIReportBtn = false;

    /** Raised when an action is blocked by the license lock; the parent decides what to show. */
    @Output() locked = new EventEmitter<void>();
    /** Raised when the AI license is invalid; the parent opens the license/plan popup. */
    @Output() licenseInvalid = new EventEmitter<void>();

    busy: Record<ServerPdfKind, boolean> = { detail: false, summary: false, ai: false };

    constructor(
        public applicationService: ApplicationService,
        private localizationService: LocalizationService) {
    }

    /**
     * True when ANY server-side PDF is being generated/downloaded. Used to disable
     * every button so only one PDF is generated per user at a time — protects
     * server CPU/RAM and avoids three indistinct downloads piling up.
     */
    get isAnyServerPdfBusy(): boolean {
        return this.busy.detail || this.busy.summary || this.busy.ai;
    }

    /** Localised label shown in place of the button text while the PDF is generating. */
    get downloadingLabel(): string {
        return this.localizationService.langCode?.startsWith('tr') ? 'İndiriliyor...' : 'Downloading...';
    }

    downloadDetail(ev: MouseEvent): void {
        this.download(ev, 'detail');
    }

    downloadSummary(ev: MouseEvent): void {
        this.download(ev, 'summary');
    }

    downloadAI(ev: MouseEvent): void {
        ev.preventDefault();
        ev.stopPropagation();
        if (!this.isAIReportLicenseValid) {
            this.licenseInvalid.emit();
            return;
        }
        this.download(ev, 'ai');
    }

    private download(ev: MouseEvent, kind: ServerPdfKind): void {
        ev.preventDefault();
        ev.stopPropagation();
        // Block re-entry while ANY server PDF is being generated.
        if (this.isAnyServerPdfBusy) return;
        if (this.isLocked) {
            this.locked.emit();
            return;
        }

        this.busy[kind] = true;
        // Overlay stays open so the user can see the spinner on the active button
        // and notice the others are disabled until generation completes.
        this.requestFor(kind).subscribe({
            next: (blob: Blob) => {
                this.applicationService.saveBlobAsFile(blob, this.fileNameFor(kind));
                this.busy[kind] = false;
            },
            error: () => {
                this.busy[kind] = false;
            }
        });
    }

    private requestFor(kind: ServerPdfKind) {
        const langCode = this.localizationService.langCode;
        switch (kind) {
            case 'detail':
                return this.applicationService.DownloadDetailPdfFromServer(this.applicationId, langCode);
            case 'summary':
                return this.applicationService.DownloadSummaryPdfFromServer(this.applicationId, langCode);
            case 'ai':
                return this.applicationService.DownloadAIPdfFromServer(this.applicationId, langCode);
        }
    }

    private fileNameFor(kind: ServerPdfKind): string {
        switch (kind) {
            case 'detail':
                return `Report_${this.applicationId}.pdf`;
            case 'summary':
                return `Summary_Report_${this.applicationId}.pdf`;
            case 'ai':
                return `AI_Report_${this.applicationId}.pdf`;
        }
    }
}
